<script lang="ts">
  import { onMount } from "svelte";
  import { api, type MyDealItem } from "../lib/api";
  import { haptic } from "../lib/tg";

  let {
    sort = "new",
    onback,
    onhome,
  }: {
    sort?: "new" | "silent" | "value";
    onback?: () => void;
    onhome?: () => void;
  } = $props();

  let items = $state<MyDealItem[]>([]);
  let total = $state(0);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let activeSort = $state<"new" | "silent" | "value">(sort);
  let showArchived = $state(false);
  let archivedTotal = $state(0);

  function moneyShort(rub: number): string {
    if (rub >= 1_000_000) return `${(rub / 1_000_000).toFixed(1).replace(/\.0$/, "")}М ₽`;
    if (rub >= 1000) return `${Math.floor(rub / 1000)}к ₽`;
    return `${rub} ₽`;
  }
  function silenceText(d: number): string {
    if (d <= 0) return "сегодня";
    if (d === 1) return "1 день молчит";
    if (d < 5) return `${d} дня молчит`;
    return `${d} дней молчит`;
  }
  function created(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("ru", { day: "numeric", month: "short" });
  }

  async function load() {
    loading = true;
    try {
      if (showArchived) {
        const r = await api.archivedDeals();
        items = r.items.map((x) => ({ ...x, created_at: null }));
        total = r.total;
        archivedTotal = r.total;
      } else {
        const [active, arch] = await Promise.all([
          api.myDeals(activeSort),
          api.archivedDeals().catch(() => ({ total: 0, items: [] })),
        ]);
        items = active.items;
        total = active.total;
        archivedTotal = arch.total;
      }
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      loading = false;
    }
  }

  async function unarchive(leadAmoId: number, ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    haptic("medium");
    try {
      await api.unarchiveDeal(leadAmoId);
      items = items.filter((x) => x.amo_id !== leadAmoId);
      archivedTotal = Math.max(0, archivedTotal - 1);
      total = items.length;
    } catch {}
  }

  function setSort(v: "new" | "silent" | "value") {
    haptic("light");
    activeSort = v;
    showArchived = false;
    history.replaceState(null, "", `?view=my-deals&sort=${v}`);
    load();
  }
  function toggleArchive() {
    haptic("light");
    showArchived = !showArchived;
    load();
  }

  onMount(load);

  const SORT_LABELS: Record<string, string> = {
    new: "новые ↓",
    silent: "молчат дольше ↓",
    value: "по сумме ↓",
  };
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">{showArchived ? "Архив" : "Мои сделки"} · {total}</div>
    <button class="home" onclick={() => window.dispatchEvent(new Event("open-deal-create"))} aria-label="Новая сделка">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
    </button>
    {#if onhome}
      <button class="home" onclick={() => onhome?.()} aria-label="Главная">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        </svg>
      </button>
    {/if}
  </header>

  <div class="sort-bar">
    {#if showArchived}
      <div class="sort-label">В архиве — скрыто из всех списков (amoCRM не тронут)</div>
      <div class="chips">
        <button class="chip" onclick={toggleArchive}>← К активным</button>
      </div>
    {:else}
      <div class="sort-label">Сортировка: <b>{SORT_LABELS[activeSort]}</b></div>
      <div class="chips">
        <button class="chip" class:active={activeSort === "new"} onclick={() => setSort("new")}>Новые</button>
        <button class="chip" class:active={activeSort === "silent"} onclick={() => setSort("silent")}>Молчат</button>
        <button class="chip" class:active={activeSort === "value"} onclick={() => setSort("value")}>По сумме</button>
        {#if archivedTotal > 0}
          <button class="chip archive-chip" onclick={toggleArchive}>Архив · {archivedTotal}</button>
        {/if}
      </div>
    {/if}
  </div>

  {#if loading}
    <div class="loading">Загружаю…</div>
  {:else if error}
    <div class="error-box">{error}</div>
  {:else if items.length === 0}
    <div class="empty">У тебя нет активных сделок</div>
  {:else}
    <section class="list">
      {#each items as l}
        <a class="row" class:hot={l.silence_days >= 14 && !showArchived} href={`?deal=${l.amo_id}`}>
          {#if !showArchived && activeSort === "silent" && l.silence_days >= 7}
            <div class="sil-badge" class:hot={l.silence_days >= 14}>
              {l.silence_days}<span class="sil-unit">дн</span>
            </div>
          {/if}
          <div class="row-main">
            <div class="row-top">
              <div class="row-text">{l.name}</div>
              {#if l.price_rub > 0}<div class="row-price">{moneyShort(l.price_rub)}</div>{/if}
            </div>
            <div class="row-meta">
              {l.stage_name} <span class="sep"></span>
              {#if showArchived}
                {silenceText(l.silence_days)}
              {:else if activeSort === "new"}
                создана {created(l.created_at)}
              {:else}
                {silenceText(l.silence_days)}
              {/if}
            </div>
          </div>
          {#if showArchived}
            <button class="unarch" type="button" aria-label="Вернуть из архива"
                    onclick={(ev) => unarchive(l.amo_id, ev)}>Вернуть</button>
          {/if}
        </a>
      {/each}
    </section>
  {/if}
</div>

<style>
  .screen { padding-bottom: 80px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute);
  }
  .back:active, .home:active { background: var(--bg-elev); }
  .back svg, .home svg { width: 20px; height: 20px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-size: 15px; font-weight: 700;
    flex: 1; text-align: center;
  }

  .sort-bar {
    padding: 12px 16px 8px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-elev);
  }
  .sort-label {
    font-size: 11px; color: var(--text-dim);
    text-transform: uppercase; letter-spacing: 0.08em;
    margin-bottom: 8px; font-weight: 600;
  }
  .sort-label b {
    color: var(--ember);
    font-weight: 700;
    text-transform: none; letter-spacing: 0;
  }
  .chips { display: flex; gap: 8px; flex-wrap: wrap; }
  .chip {
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 22px;
    padding: 9px 16px;
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
    font-family: 'Manrope', sans-serif;
    -webkit-tap-highlight-color: rgba(232,124,47,0.15);
  }
  .chip:active { transform: scale(0.95); }
  .chip.active {
    background: var(--ember);
    color: #fff;
    border-color: var(--ember);
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(232,124,47,0.32);
  }
  .chip.archive-chip {
    margin-left: auto;
    color: var(--text-mute);
    border-color: var(--text-dim);
  }
  .unarch {
    margin-left: auto;
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    color: var(--text-mute);
    border-radius: 10px;
    padding: 6px 10px;
    font-size: 12px; font-weight: 600;
    cursor: pointer;
    align-self: center;
  }
  .unarch:active { transform: scale(0.94); }
  .unarch:hover { color: var(--text); border-color: var(--ember); }

  .loading, .error-box, .empty { padding: 40px 16px; text-align: center; color: var(--text-mute); font-size: 13px; }
  .error-box { color: var(--danger); }

  .list { padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; }
  .row {
    display: flex; gap: 10px; align-items: center;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    color: var(--text);
    text-decoration: none;
    box-shadow: var(--shadow-sm);
    transition: transform 80ms ease;
  }
  .row:active { transform: scale(0.985); }
  .row.hot { border-left: 3px solid var(--danger); }
  .sil-badge {
    flex-shrink: 0;
    min-width: 44px; height: 44px;
    border-radius: 10px;
    background: var(--warning);
    color: #0E0E10;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 15px;
    line-height: 1;
    padding: 0 6px;
  }
  .sil-badge.hot { background: var(--danger); color: #fff; }
  .sil-unit { font-size: 9px; opacity: 0.85; margin-top: 2px; font-weight: 500; }
  .row-main { flex: 1; min-width: 0; }
  .row-top {
    display: flex; justify-content: space-between; align-items: center;
    gap: 10px;
  }
  .row-text {
    font-family: 'Manrope', sans-serif;
    font-weight: 600; font-size: 14px;
  }
  .row-price {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px; font-weight: 600;
    color: var(--brass);
    flex-shrink: 0;
  }
  .row-meta {
    margin-top: 4px;
    font-size: 11px; color: var(--text-mute);
    display: flex; align-items: center; gap: 8px;
    flex-wrap: wrap;
  }
  .sep { width: 3px; height: 3px; background: var(--text-dim); border-radius: 50%; }
</style>
